import React, { useState } from 'react';
import {
  Box, Container, Grid, Typography, Paper, Stack,
  useTheme, useMediaQuery, Card, CardContent,
  Fade, Zoom, Grow, Slide, alpha, Chip, Divider,
  Button, IconButton, Avatar, List, ListItem,
  ListItemIcon, ListItemText, Tabs, Tab
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

import {
  NotificationsActive,
  Sms,
  Smartphone,
  Settings,
  VolumeUp,
  CallMissed,
  CheckCircle,
  AccessTime,
  Message,
  PhoneMissed,
  ArrowForward,
  PlayArrow,
  Download,
  Share,
  Speed,
  Security,
  CloudQueue,
  Analytics,
  Sync,
  Bolt,
  Verified,
  TrendingUp,
  Group,
  QrCode,
  NotificationImportant,
  RingVolume,
  Vibration
} from '@mui/icons-material';

// Animations
const floatAnimation = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulseAnimation = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px ${alpha('#4551be', 0.3)};
  }
  50% { 
    box-shadow: 0 0 40px ${alpha('#4551be', 0.5)};
  }
`;

const slideIn = keyframes`
  from { 
    transform: translateX(-30px); 
    opacity: 0; 
  }
  to { 
    transform: translateX(0); 
    opacity: 1; 
  }
`;

// Styled Components
const ModernHero = styled(Box)(({ theme }) => ({
  minHeight: '80h',
  background: `linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)`, // Changed to light background
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  color: '#0f172a', // Changed to dark text
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 50%, ${alpha('#4551be', 0.1)} 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, ${alpha('#7b1fa2', 0.1)} 0%, transparent 50%)
    `,
  }
}));

const FloatingNotification = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '300px',
  height: '100px',
  background: 'rgba(255, 255, 255, 0.9)', // Light background
  backdropFilter: 'blur(20px)',
  borderRadius: '20px',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
  padding: '20px',
  animation: `${floatAnimation} 6s ease-in-out infinite`,
  zIndex: 1,
  '&:nth-of-type(1)': {
    top: '20%',
    left: '10%',
    animationDelay: '0s',
  },
  '&:nth-of-type(2)': {
    top: '60%',
    right: '10%',
    animationDelay: '2s',
  }
}));

const ModernCard = styled(Card)(({ theme, color }) => ({
  background: 'rgba(255, 255, 255, 0.8)', // Light card background
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  border: `1px solid ${alpha(color || theme.palette.primary.main, 0.2)}`,
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(90deg, ${color || theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
  '&:hover': {
    transform: 'translateY(-12px)',
    borderColor: color || theme.palette.primary.main,
    boxShadow: `0 25px 60px ${alpha(color || theme.palette.primary.main, 0.15)}`,
    '& .card-icon': {
      transform: 'scale(1.1) rotate(10deg)',
      animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
    }
  }
}));

const FeatureBadge = styled(Chip)(({ theme }) => ({
  background: alpha(theme.palette.primary.main, 0.1),
  color: theme.palette.primary.main,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  fontWeight: 600,
  fontSize: '12px',
  padding: '6px 12px',
  '& .MuiChip-icon': {
    color: theme.palette.primary.main
  }
}));

const Alert = () => {
  const theme = useTheme();

    const img1 = `${process.env.PUBLIC_URL}/images/bg55.jpg`;

  const alertTypes = [
    {
      icon: <NotificationImportant />,
      title: 'Real-time Pop-up Alerts',
      description: 'Instant visual notifications that appear on your screen with caller details and timestamps. Never miss important calls even while working.',
      color: '#4551be',
      features: ['Instant visibility', 'Caller information', 'Non-intrusive', 'Quick action buttons']
    },
    {
      icon: <Sms />,
      title: 'SMS Notification System',
      description: 'Direct SMS delivery with complete caller information. Works without internet, ensuring you stay connected anywhere, anytime.',
      color: '#4caf50',
      features: ['No internet needed', 'Detailed information', 'Reliable delivery', 'Offline access']
    },
    {
      icon: <Smartphone />,
      title: 'Mobile App Push',
      description: 'Integrated push notifications through our dedicated app. Get alerts with rich previews and quick response options.',
      color: '#ff9800',
      features: ['Rich notifications', 'Quick responses', 'App integration', 'Media support']
    },
    {
      icon: <RingVolume />,
      title: 'Custom Alert Profiles',
      description: 'Create personalized alert settings for different contacts and situations. Set unique tones, vibrations and priorities.',
      color: '#9c27b0',
      features: ['Personalized tones', 'Contact-based alerts', 'Priority settings', 'Schedule alerts']
    }
  ];

  const benefits = [
    {
      icon: <Speed />,
      title: 'Lightning Fast',
      description: 'Receive alerts within seconds of a missed call'
    },
    {
      icon: <Security />,
      title: 'Secure & Private',
      description: 'End-to-end encryption for all your notifications'
    },
    {
      icon: <Sync />,
      title: 'Sync Across Devices',
      description: 'Alerts appear on all your connected devices'
    },
    {
      icon: <Analytics />,
      title: 'Smart Analytics',
      description: 'Track missed calls and response patterns'
    }
  ];

  const useCases = [
    {
      scenario: 'Business Professionals',
      description: 'Never miss client calls during meetings',
      icon: <Group />
    },
    {
      scenario: 'Remote Teams',
      description: 'Stay connected with team members worldwide',
      icon: <CloudQueue />
    },
    {
      scenario: 'Emergency Services',
      description: 'Immediate alerts for critical situations',
      icon: <Bolt />
    },
    {
      scenario: 'Sales Teams',
      description: 'Follow up instantly with potential clients',
      icon: <TrendingUp />
    }
  ];

  const stats = [
    { value: '99.9%', label: 'Delivery Rate', icon: <Verified /> },
    { value: '<2s', label: 'Alert Speed', icon: <Speed /> },
    { value: '24/7', label: 'Uptime', icon: <AccessTime /> },
    { value: '10K+', label: 'Active Users', icon: <Group /> }
  ];

  return (
    <Box sx={{
      background: '#f0f4f8', // Changed to light background
      color: '#0f172a', // Changed to dark text
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Navbar />
      
      {/* Animated Background Elements */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        overflow: 'hidden'
      }}>
        {[...Array(20)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              background: `radial-gradient(circle, ${alpha('#4551be', 0.05)} 0%, transparent 70%)`, // Changed to blue accent
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `${floatAnimation} ${15 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              filter: 'blur(2px)'
            }}
          />
        ))}
      </Box>

      {/* Hero Section */}
      <ModernHero>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center" sx={{ position: 'relative', zIndex: 2,py:11 }}>
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <FeatureBadge 
                    icon={<CallMissed />}
                    label="MISSED CALL ALERT SYSTEM"
                    sx={{ mb: 4, background: 'rgba(69,81,190,0.1)', color: '#4551be', border: '1px solid rgba(69,81,190,0.2)' }}
                  />

                  {/* Logo + Title in Same Row */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    {/* Logo */}
                    <Box
                      component="img"
                      src="/images/miss.png"
                      alt="Missed Call Alert Logo"
                      sx={{
                        width: { xs: 190, md: 190 },
                        height: { xs: 90, md: 130 },
                        objectFit: 'contain',
                        borderRadius: 3,
                        bgcolor: 'rgba(255,255,255,0.7)', // Light background
                        p: 0.5,
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(0,0,0,0.1)',
                        flexShrink: 0,
                      }}
                    />

                    {/* Title */}
                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: {
                          xs: '2.2rem',
                          md: '3.2rem',
                          lg: '3.8rem',
                        },
                        fontWeight: 900,
                        lineHeight: 1.1,
                        color: '#4551be', // Changed to blue
                      }}
                    >
                      Never Miss

                      <Box
                        component="span"
                        sx={{
                          display: 'block',
                          color: '#4551be', // Changed to blue
                        }}
                      >
                        Important Calls
                      </Box>
                    </Typography>
                  </Box>

                  <Typography sx={{
                    color: '#475569', // Changed to dark grey
                    mb: 5,
                    fontSize: '1.2rem',
                    lineHeight: 1.6,
                    maxWidth: '90%'
                  }}>
                    Stay connected with our intelligent missed call alert system. 
                    Instant notifications, multiple delivery channels and smart 
                    features ensure you never miss vital communications.
                  </Typography>

                  {/* Stats */}
                  {/* <Grid container spacing={4} sx={{ maxWidth: '600px' }}>
                    {stats.map((stat, index) => (
                      <Grid item xs={6} key={index}>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box sx={{
                            width: 50,
                            height: 50,
                            borderRadius: '12px',
                            background: alpha('#4551be', 0.1), // Changed to blue accent
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#4551be', // Changed to blue
                            animation: `${floatAnimation} ${4 + index * 0.5}s ease-in-out infinite`
                          }}>
                            {stat.icon}
                          </Box>
                          <Box>
                            <Typography variant="h4" sx={{ 
                              fontWeight: 900, 
                              mb: 0.5,
                              color: '#1e293b' // Dark text
                            }}>
                              {stat.value}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: '#64748b', // Dark grey
                              fontWeight: 500
                            }}>
                              {stat.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid> */}
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{ position: 'relative', height: 500 }}>
                  {/* Floating Notifications */}
                  <FloatingNotification>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ 
                        bgcolor: alpha('#4551be', 0.1),
                        color: '#4551be'
                      }}>
                        <PhoneMissed />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          Missed Call Alert
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          John Smith • 2 minutes ago
                        </Typography>
                      </Box>
                    </Stack>
                  </FloatingNotification>

                  <FloatingNotification sx={{ animationDelay: '3s' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ 
                        bgcolor: alpha('#4caf50', 0.1),
                        color: '#4caf50'
                      }}>
                        <Sms />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                          SMS Notification
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          +1 (555) 123-4567
                        </Typography>
                      </Box>
                    </Stack>
                  </FloatingNotification>

                  {/* Main Notification Card */}
                  <Box sx={{
                    position: 'relative',
                    background: 'rgba(255, 255, 255, 0.8)', // Light background
                    backdropFilter: 'blur(30px)',
                    borderRadius: '28px',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    p: 4,
                    height: '100%',
                    boxShadow: '0 40px 80px rgba(0, 0, 0, 0.1)',
                    animation: `${floatAnimation} 8s ease-in-out infinite`
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: 'radial-gradient(circle, #4551be, transparent)', // Changed to blue
                      opacity: 0.1,
                      filter: 'blur(20px)'
                    }} />
                    
                    <Typography variant="h5" sx={{ 
                      fontWeight: 700,
                      mb: 3,
                      color: '#1e293b' // Dark text
                    }}>
                      Smart Alert Dashboard
                    </Typography>

                    <Stack spacing={3}>
                      {[
                        { type: 'VIP Call', time: 'Just now', status: 'Missed', color: '#ff4081' },
                        { type: 'Team Call', time: '5 min ago', status: 'Alerted', color: '#4caf50' },
                        { type: 'Client Call', time: '15 min ago', status: 'Notified', color: '#ff9800' },
                        { type: 'Unknown', time: '1 hr ago', status: 'Archived', color: '#9e9e9e' }
                      ].map((alert, index) => (
                        <Box key={index} sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          borderRadius: '12px',
                          background: 'rgba(0, 0, 0, 0.02)',
                          border: '1px solid rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'rgba(0, 0, 0, 0.04)',
                            transform: 'translateX(5px)'
                          }
                        }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Box sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: alert.color,
                              boxShadow: `0 0 10px ${alert.color}`
                            }} />
                            <Box>
                              <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
                                {alert.type}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#64748b' }}>
                                {alert.time}
                              </Typography>
                            </Box>
                          </Stack>
                          <Chip
                            label={alert.status}
                            size="small"
                            sx={{
                              background: alpha(alert.color, 0.1),
                              color: alert.color,
                              border: `1px solid ${alpha(alert.color, 0.3)}`,
                              fontWeight: 600
                            }}
                          />
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </ModernHero>

      {/* Alert Types Section */}
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}>
            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              mb: 3,
              color: '#4551be', // Changed to blue
            }}>
              Smart Alert Systems
            </Typography>
            <Typography sx={{
              color: '#64748b', // Dark grey
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: '1.2rem'
            }}>
              Multiple notification channels to ensure you never miss important calls. 
              Choose the alert method that fits your workflow.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {alertTypes.map((alert, index) => (
              <Grid item xs={12} sm={6} lg={3} key={index}>
                <Slide direction="up" in timeout={800} style={{ transitionDelay: `${index * 100}ms` }}>
                  <ModernCard color={alert.color}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '20px',
                        background: alpha(alert.color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 24px auto',
                        color: alert.color,
                        transition: 'all 0.4s ease'
                      }} className="card-icon">
                        {alert.icon}
                      </Box>

                      <Typography variant="h5" sx={{ 
                        fontWeight: 800,
                        mb: 2,
                        color: '#1e293b' // Dark text
                      }}>
                        {alert.title}
                      </Typography>

                      <Typography sx={{ 
                        color: '#64748b', // Dark grey
                        mb: 3,
                        lineHeight: 1.6,
                        fontSize: '0.95rem'
                      }}>
                        {alert.description}
                      </Typography>

                      <Stack spacing={1} sx={{ mb: 3 }}>
                        {alert.features.map((feature, idx) => (
                          <Box key={idx} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            p: 1,
                            borderRadius: '8px',
                            background: alpha(alert.color, 0.05)
                          }}>
                            <CheckCircle sx={{ fontSize: 16, color: alert.color }} />
                            <Typography variant="caption" sx={{ color: '#475569' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Button
                        endIcon={<ArrowForward />}
                        sx={{
                          color: alert.color,
                          fontWeight: 600,
                          fontSize: '14px'
                        }}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </ModernCard>
                </Slide>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ 
        py: { xs: 8, md: 12 },
        background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)' // Changed to light background
      }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Typography variant="h2" sx={{
                    fontSize: { xs: '2.5rem', md: '3rem' },
                    fontWeight: 900,
                    mb: 4,
                    color: '#1e293b' // Dark text
                  }}>
                    Why Choose Our Alert System
                  </Typography>
                  
                  <Typography sx={{ 
                    color: '#64748b', // Dark grey
                    mb: 5,
                    lineHeight: 1.7,
                    fontSize: '1.1rem'
                  }}>
                    We understand the pace of modern business. Our intelligent alert system 
                    ensures you stay connected, responsive and never miss vital opportunities.
                  </Typography>

                  <Stack spacing={3}>
                    {benefits.map((benefit, index) => (
                      <Box key={index} sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        p: 3,
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.8)', // Light background
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 1)',
                          transform: 'translateX(10px)'
                        }
                      }}>
                        <Box sx={{
                          width: 60,
                          height: 60,
                          borderRadius: '15px',
                          background: alpha('#4551be', 0.1), // Changed to blue accent
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#4551be', // Changed to blue
                          flexShrink: 0
                        }}>
                          {benefit.icon}
                        </Box>
                        <Box>
                          <Typography variant="h6" sx={{ 
                            fontWeight: 700,
                            mb: 1,
                            color: '#1e293b' // Dark text
                          }}>
                            {benefit.title}
                          </Typography>
                          <Typography sx={{ 
                            color: '#64748b', // Dark grey
                            lineHeight: 1.6
                          }}>
                            {benefit.description}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box>
                  <ModernCard>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" gutterBottom sx={{ 
                        fontWeight: 700,
                        mb: 4,
                        color: '#4551be' // Changed to blue
                      }}>
                        Perfect For
                      </Typography>

                      <Grid container spacing={3}>
                        {useCases.map((useCase, index) => (
                          <Grid item xs={12} sm={6} key={index}>
                            <Box sx={{ 
                              textAlign: 'center',
                              p: 3,
                              borderRadius: '16px',
                              background: 'rgba(255, 255, 255, 0.8)', // Light background
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              height: '100%'
                            }}>
                              <Box sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '15px',
                                background: alpha('#4551be', 0.1), // Changed to blue accent
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px auto',
                                color: '#4551be' // Changed to blue
                              }}>
                                {useCase.icon}
                              </Box>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 700,
                                mb: 1,
                                fontSize: '1.1rem',
                                color: '#1e293b' // Dark text
                              }}>
                                {useCase.scenario}
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                color: '#64748b' // Dark grey
                              }}>
                                {useCase.description}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </ModernCard>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: { xs: 10, md: 15 },
        background: 'linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%)', // Changed to light background
        color: '#0f172a', // Changed to dark text
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${img1})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.05,
          filter: 'blur(2px)'
        }} />
        
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 100,
              height: 100,
              borderRadius: '25px',
              background: alpha('#4551be', 0.1), // Changed to blue accent
              mb: 4,
              border: `2px solid ${alpha('#4551be', 0.2)}`,
              animation: `${pulseAnimation} 3s ease-in-out infinite`
            }}>
              <NotificationsActive sx={{ fontSize: 48, color: '#4551be' }} />
            </Box>

            <Typography variant="h2" sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 900,
              mb: 3,
              color: '#1e293b' // Dark text
            }}>
              Stay Connected Always
            </Typography>
            
            <Typography sx={{
              maxWidth: '800px',
              mx: 'auto',
              mb: 6,
              fontSize: '1.2rem',
              color: '#64748b', // Dark grey
              lineHeight: 1.6,
              opacity: 0.9
            }}>
              Don't miss important opportunities. Our intelligent alert system ensures 
              you're always informed, responsive and ready for every call that matters.
            </Typography>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default Alert;